// GTM interactions helpers: build standard payloads and bind global click tracking

function inferLabel(el) {
  const dataLabel = el.dataset.gtmLabel;
  if (dataLabel && dataLabel.trim()) {
    return dataLabel.trim();
  }

  const aria = el.getAttribute('aria-label');
  if (aria && aria.trim()) {
    return aria.trim();
  }

  const text = el.textContent || '';
  return text.replace(/\s+/g, ' ').trim();
}

function inferContext(el) {
  const withContext = el.closest('[data-gtm-context]');
  if (withContext?.dataset.gtmContext) {
    return withContext.dataset.gtmContext.trim();
  }

  const labelAsContext = inferLabel(el);
  if (labelAsContext) {
    return labelAsContext;
  }

  const block = el.closest('[data-block]');
  if (block?.dataset.block) return block.dataset.block;

  const section = el.closest('section[class], [class*="block"], .wp-block');
  if (section?.className) {
    const firstClass = section.className.trim().split(/\s+/)[0];
    if (firstClass) return firstClass;
  }

  return '';
}

function inferType(el) {
  const dataType = el.dataset.gtmType;
  if (dataType && dataType.trim()) {
    return dataType.trim();
  }

  if (el.closest('.header') || el.closest('.header__sub-menu-root') || el.closest('.header__mobile')) {
    return 'Menu';
  }

  if (el.closest('.footer')) {
    return 'Footer';
  }

  if (el.closest('.filter-bar')) {
    return 'Search';
  }

  if (el.closest('.c-custom-select')) {
    return 'Filter';
  }

  if (el.closest('main')) {
    return 'Button';
  }

  return '';
}

export function buildInteractionPayload(el, overrides = {}) {
  const type = overrides.type || inferType(el);
  const context = overrides.context ?? inferContext(el);
  const label = overrides.label ?? inferLabel(el);

  if (!type || !label) {
    return null;
  }

  return {
    event: 'interactions',
    eventTimestamp: Date.now(),
    type,
    context,
    label,
    ...(overrides.extra || {}),
  };
}

let isGlobalTrackingBound = false;

export function bindGlobalInteractionTracking() {
  if (isGlobalTrackingBound || typeof document === 'undefined') return;
  isGlobalTrackingBound = true;

  const bindTextInputTracking = () => {
    const textInputs = document.querySelectorAll('input[type="text"]');

    textInputs.forEach((input) => {
      const sendInteraction = (value) => {
        if (!window.dataLayer) return;
        const trimmed = String(value || '').trim();
        if (!trimmed) return;

        const typeFromAria = input.getAttribute('aria-label')?.trim() || 'Input';
        const payload = buildInteractionPayload(input, {
          type: typeFromAria,
          label: trimmed,
        });

        if (!payload) return;

        window.dataLayer.push(payload);
      };

      input.addEventListener('input', (e) => sendInteraction(e.target.value));
      input.addEventListener('keydown', (e) => {
        if (e.key === 'Enter') sendInteraction(input.value);
      });
    });
  };

  document.body.addEventListener('click', (event) => {
    if (!window.dataLayer) return;

    const target = event.target.closest('a, button, [data-gtm-click]') || event.target.closest('.c-custom-select__options li, .c-custom-select__clear');
    if (!target) return;

    const select = target.closest('.c-custom-select');
    if (select) {
      // Defer to allow c-custom-select internal handlers to update state first
      setTimeout(() => {
        const activeItems = select.querySelectorAll('.c-custom-select__options li.active');
        const filterValues = Array.from(activeItems)
          .filter((li) => !li.classList.contains('c-custom-select__group--sub'))
          .map((li) => li.textContent.trim())
          .filter(Boolean);

        // If nothing selected after click (e.g. clear), still send empty state as clearing filters
        const payload = buildInteractionPayload(select, {
          type: 'Filter',
          label: filterValues.join(', '),
        });

        if (!payload) return;

        window.dataLayer.push(payload);
      }, 0);
      return;
    }

    const payload = buildInteractionPayload(target);
    if (!payload) return;

    window.dataLayer.push(payload);
  }, { capture: true });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bindTextInputTracking);
  } else {
    bindTextInputTracking();
  }
}
